import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Tour } from './tour.types';

/**
 * Service for managing recommended tours for the current page/context.
 * 
 * Supports two modes:
 * 1. Manual: Components call setRecommendedTours() to suggest specific tours
 * 2. Automatic: Tours with suggestedRoutes are automatically recommended when the route matches
 * 
 * Manual recommendations take priority over automatic ones.
 * 
 * Usage in components (manual mode):
 * ```
 * ngOnInit() {
 *   this.recommendedTourService.setRecommendedTours(['welcome', 'businesses']);
 * }
 * 
 * ngOnDestroy() {
 *   this.recommendedTourService.clearRecommendedTours();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class RecommendedTourService {
  private recommendedTourIdsSubject = new BehaviorSubject<string[]>([]);
  public recommendedTourIds$: Observable<string[]> = this.recommendedTourIdsSubject.asObservable();
  private debounceTimer: any = null;
  private lastRoute: string = '';
  
  // Track whether manual recommendations are active (takes priority over auto)
  private manualRecommendationsActive = false;
  
  // Reference to all tours (set by TourService)
  private allTours: Tour[] = [];

  constructor(private router: Router) {
    // Listen for route changes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navigationEnd = event as NavigationEnd;
        // Extract route path without query params
        const currentRoute = navigationEnd.urlAfterRedirects.split('?')[0];
        
        // If the route path has changed (not just query params)
        if (this.lastRoute && currentRoute !== this.lastRoute) {
          // Clear any pending debounce timers
          if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
          }
          
          // Reset manual flag when route changes
          this.manualRecommendationsActive = false;
          
          // Auto-detect tours for new route
          this.autoDetectTours(currentRoute);
        }
        
        this.lastRoute = currentRoute;
      });
  }

  /**
   * Register all tours for automatic route matching.
   * Called by TourService during initialization.
   */
  registerTours(tours: Tour[]): void {
    this.allTours = tours;
    // Trigger initial auto-detection for current route
    const currentRoute = this.router.url.split('?')[0];
    if (currentRoute) {
      this.autoDetectTours(currentRoute);
    }
  }

  /**
   * Automatically detect and recommend tours based on current route.
   * Only runs when manual recommendations are not active.
   */
  private autoDetectTours(currentRoute: string): void {
    if (this.manualRecommendationsActive) {
      return;
    }

    const matchingTourIds = this.allTours
      .filter(tour => {
        // Must have suggested routes that match
        if (!tour.suggestedRoutes || !this.routeMatchesAny(currentRoute, tour.suggestedRoutes)) {
          return false;
        }
        // Must NOT match any exclude routes
        if (tour.excludeRoutes && this.routeMatchesAny(currentRoute, tour.excludeRoutes)) {
          return false;
        }
        return true;
      })
      .map(tour => tour.id);

    // Update without debounce for auto-detection (already debounced by NavigationEnd)
    this.recommendedTourIdsSubject.next(matchingTourIds);
  }

  /**
   * Check if a route matches any of the patterns.
   */
  private routeMatchesAny(route: string, patterns: string[]): boolean {
    return patterns.some(pattern => this.routeMatches(route, pattern));
  }

  /**
   * Check if a route matches a single pattern.
   * Supports:
   * - Exact match: '/myagency/setup'
   * - Single wildcard: '/myagency/*' (matches one segment)
   * - Deep wildcard: '/myagency/**' (matches any depth)
   * - Dynamic segments: '/app/:id' (matches any value)
   */
  private routeMatches(route: string, pattern: string): boolean {
    // Normalize routes (remove trailing slashes)
    const normalizedRoute = route.replace(/\/$/, '') || '/';
    const normalizedPattern = pattern.replace(/\/$/, '') || '/';

    // Handle deep wildcard at the end
    if (normalizedPattern.endsWith('/**')) {
      const prefix = normalizedPattern.slice(0, -3);
      return normalizedRoute === prefix || normalizedRoute.startsWith(prefix + '/');
    }

    // Split into segments
    const routeSegments = normalizedRoute.split('/').filter(s => s);
    const patternSegments = normalizedPattern.split('/').filter(s => s);

    // If pattern has single wildcard at end, route must have at least as many segments
    if (patternSegments[patternSegments.length - 1] === '*') {
      if (routeSegments.length < patternSegments.length) {
        return false;
      }
      // Check all segments except the wildcard
      for (let i = 0; i < patternSegments.length - 1; i++) {
        if (!this.segmentMatches(routeSegments[i], patternSegments[i])) {
          return false;
        }
      }
      return true;
    }

    // For exact match patterns, segment count must match
    if (routeSegments.length !== patternSegments.length) {
      return false;
    }

    // Check each segment
    return patternSegments.every((patternSeg, i) => 
      this.segmentMatches(routeSegments[i], patternSeg)
    );
  }

  /**
   * Check if a route segment matches a pattern segment.
   */
  private segmentMatches(routeSegment: string, patternSegment: string): boolean {
    // Dynamic segment (starts with :)
    if (patternSegment.startsWith(':')) {
      return true;
    }
    // Wildcard matches anything
    if (patternSegment === '*') {
      return true;
    }
    // Exact match (case-insensitive)
    return routeSegment.toLowerCase() === patternSegment.toLowerCase();
  }

  /**
   * Set the recommended tour IDs for the current page/context.
   * This will trigger the tour suggestions component to update.
   * Debounced to prevent flashing when tours are rapidly set/cleared.
   * 
   * Manual recommendations take priority over automatic route-based ones.
   * 
   * @param tourIds Array of tour IDs to recommend
   */
  setRecommendedTours(tourIds: string[]): void {
    // Mark as manual to prevent auto-detection from overriding
    this.manualRecommendationsActive = true;
    
    // Clear any pending debounce
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Debounce the update to prevent flashing
    this.debounceTimer = setTimeout(() => {
      this.recommendedTourIdsSubject.next(tourIds);
      this.debounceTimer = null;
    }, 150);
  }

  /**
   * Clear all recommended tours.
   * Typically called in ngOnDestroy to clean up when leaving a page.
   * Debounced to prevent flashing when tours are rapidly set/cleared.
   * 
   * After clearing, automatic route-based detection will resume.
   */
  clearRecommendedTours(): void {
    // Clear any pending debounce
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Debounce the update to prevent flashing
    this.debounceTimer = setTimeout(() => {
      this.manualRecommendationsActive = false;
      // After clearing manual, auto-detect based on current route
      const currentRoute = this.router.url.split('?')[0];
      this.autoDetectTours(currentRoute);
      this.debounceTimer = null;
    }, 150);
  }

  /**
   * Get the current recommended tour IDs.
   */
  getRecommendedTours(): string[] {
    return this.recommendedTourIdsSubject.value;
  }

  /**
   * Add a single tour to the recommended list.
   * Useful for conditional recommendations.
   */
  addRecommendedTour(tourId: string): void {
    this.manualRecommendationsActive = true;
    const current = this.recommendedTourIdsSubject.value;
    if (!current.includes(tourId)) {
      this.recommendedTourIdsSubject.next([...current, tourId]);
    }
  }

  /**
   * Remove a single tour from the recommended list.
   */
  removeRecommendedTour(tourId: string): void {
    const current = this.recommendedTourIdsSubject.value;
    this.recommendedTourIdsSubject.next(current.filter(id => id !== tourId));
  }
}
